/** Portfolio (V3Bots): every bot in one table. */
import { Crew } from "../components/Crew.tsx";
import { Shell } from "../components/Shell.tsx";
import { Empty, Loading, Page, Stat, StatusChip } from "../components/ui.tsx";
import { useApi } from "../lib/data.ts";
import { pct, signedUsd, usd } from "../lib/format.ts";
import type { Overview } from "../lib/overview.ts";
import { Link, navigate } from "../lib/router.tsx";

export function Portfolio() {
  const { data, loading } = useApi<Overview>("/overview", { refreshMs: 60_000 });
  const bots = data?.bots ?? [];
  const total = bots.reduce((s, b) => s + (b.last_equity ?? b.starting_balance), 0), start = bots.reduce((s, b) => s + b.starting_balance, 0);
  return (
    <Shell crumb="Bots · Portfolio" actions={<Link to="/bots/new" className="btn-primary no-underline hover:no-underline">New bot</Link>}>
      <Page eyebrow="Bots" title="Portfolio" width={1100}>
        {loading ? <Loading /> : !bots.length ? <Empty title="No bots yet">Start one on paper from a template.</Empty> : (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(170px,100%),1fr))] gap-2.5">
              <Stat label="Total value" value={usd(total)} />
              <Stat label="Started with" value={usd(start)} />
              <Stat label="Since start" value={pct(start ? (total / start - 1) * 100 : 0)} tone={total >= start ? "up" : "down"} />
              <Stat label="Bots running" value={`${bots.filter((b) => b.status === "active").length} of ${bots.length}`} />
            </div>
            <div className="card overflow-x-auto">
              <table className="table min-w-[760px]">
                <thead><tr><th>Bot</th><th>Strategy</th><th>Status</th><th className="text-right">Value</th><th className="text-right">Today</th><th className="text-right">Since start</th><th className="text-right" title="Drawdown: how far below its best value">Below best</th></tr></thead>
                <tbody>
                  {bots.map((b) => {
                    const v = b.last_equity ?? b.starting_balance, d = b.last_equity !== null && b.equity_24h !== null ? b.last_equity - b.equity_24h : null;
                    return (
                      <tr key={b.id} className="cursor-pointer hover:bg-soft/60" onClick={() => navigate(`/bots/${b.id}`)}>
                        <td><Link to={`/bots/${b.id}`} className="flex items-center gap-2.5 font-medium text-ink-strong no-underline"><Crew character={b.character} size={24} />{b.name}</Link></td>
                        <td className="text-muted">{b.strategyName}</td>
                        <td><StatusChip status={b.status} mode={b.mode} exchange={b.exchange} /></td>
                        <td className="num text-right">{usd(v)}</td>
                        <td className={`num text-right ${d === null ? "" : d >= 0 ? "up" : "down"}`}>{d === null ? "—" : signedUsd(d)}</td>
                        <td className={`num text-right ${v >= b.starting_balance ? "up" : "down"}`}>{pct((v / b.starting_balance - 1) * 100)}</td>
                        <td className="num text-right">{b.drawdown === null ? "—" : `${(b.drawdown * 100).toFixed(1)}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Page>
    </Shell>
  );
}
